// The story's content (gate D1.3), read from the tables: the quests, the story's rules (variables, arcs, deed kinds,
// the town faction, affinity for the three townsfolk, deed rules, triggers, spawn groups, the barks' gates, the scenes'
// stand-in lengths), the barks and who talks through which ink script. References resolve to string ids here; the
// conditions stay in their string form (the story parses them). No browser-only imports: the Node tools read it too.
import type { BarkDef, FactionDef, AffinityRules, DeedRule, QuestDef, StoryAction, TriggerDef, FactValue } from "@rpg/story";
import type { ContentRegistry } from "@rpg/content";
import { readAction, readQuests, type ActionRow, type QuestRow } from "@rpg-unit/quest-journal";
import type { ContentTables } from "../content/types.gen";

type Row<T extends keyof ContentTables> = ContentTables[T];

/** A group of enemies a story action spawns. */
export interface SpawnGroup {
  id: string;
  at: [number, number];
  spread: number;
  tag: string;
  enemies: { kind: string; count: number }[];
}

/** Who talks through which script. */
export interface DialogueDef {
  npc: string;
  script: string;
  knot: string;
  verb: string;
  reach: number;
  priority: number;
}

/** Everything the story reads. */
export interface StoryContent {
  quests: QuestDef[];
  vars: Record<string, FactValue>;
  arcs: Record<string, string[]>;
  deedKinds: string[];
  hosts: string[];
  factions: FactionDef[];
  affinity: AffinityRules;
  talk: { affinity: number };
  deedRules: DeedRule[];
  triggers: TriggerDef[];
  groups: SpawnGroup[];
  barks: BarkDef[];
  barkGates: { speakerGap: number; gap: number; cooldown: number };
  barkRadius: number;
  witnessRadius: number;
  logCap: number;
  scenes: Record<string, number>;
  dialogues: DialogueDef[];
}

/** The story row this game uses. */
export const STORY_ROW = "green-valley";

// actions (a quest's, a deed rule's, a trigger's) read by @rpg-unit/quest-journal's reader (the schema's flat shape back
// to @rpg/story's union)
const actions = (list: readonly unknown[] | undefined): StoryAction[] | undefined => (list ? list.map((a) => readAction(a as ActionRow)) : undefined);

/** Read the story's tables. Throws naming what is missing. */
export function readStoryContent(reg: ContentRegistry<ContentTables>): StoryContent {
  const row = reg.get("story", STORY_ROW);
  if (!row) throw new Error(`story: no story row "${STORY_ROW}"`);
  // the quests: rows read into @rpg/story's quests by @rpg-unit/quest-journal
  const quests: QuestDef[] = readQuests(reg.rows("quests") as unknown as QuestRow[]);
  const barks: BarkDef[] = reg.rows("barks").map((b) => ({
    id: b.id,
    speaker: b.speaker,
    line: b.line.id,
    ...(b.when ? { when: b.when } : {}),
    ...(b.deed ? { deed: { kind: b.deed.kind, within: b.deed.within, ...(b.deed.subject !== undefined ? { subject: b.deed.subject } : {}), ...(b.deed.place !== undefined ? { place: b.deed.place } : {}), ...(b.deed.near !== undefined ? { near: b.deed.near } : {}) } } : {}),
    ...(b.priority !== undefined ? { priority: b.priority } : {}),
    ...(b.cooldown !== undefined ? { cooldown: b.cooldown } : {}),
    ...(b.once !== undefined ? { once: b.once } : {}),
  }));
  const dialogues: DialogueDef[] = reg.rows("dialogues").map((d) => ({ npc: d.id, script: d.script, knot: d.knot, verb: d.verb.id, reach: d.reach, priority: d.priority }));
  return {
    quests,
    vars: { ...(row.vars as Record<string, FactValue>) },
    arcs: Object.fromEntries(Object.entries(row.arcs).map(([k, v]) => [k, [...(v as string[])]])),
    deedKinds: [...row.deedKinds],
    hosts: [...row.hosts],
    factions: row.factions.map((f) => ({ id: f.id, ...(f.start !== undefined ? { start: f.start } : {}), range: [f.range[0]!, f.range[1]!], tiers: f.tiers.map((t) => ({ id: t.id, min: t.min, ...(t.perks ? { perks: { ...(t.perks as Record<string, number>) } } : {}) })) })),
    affinity: {
      npcs: row.affinity.npcs.map((n) => ({ id: n.id, ...(n.start !== undefined ? { start: n.start } : {}), ...(n.gifts ? { gifts: { ...n.gifts } } : {}) })),
      range: [row.affinity.range[0]!, row.affinity.range[1]!],
      gift: { ...row.affinity.gift },
      tiers: row.affinity.tiers.map((t) => ({ id: t.id, min: t.min })),
    },
    talk: { ...row.talk },
    deedRules: row.deedRules.map((r) => ({ on: { ...r.on }, ...(r.affinity ? { affinity: { ...(r.affinity as Record<string, number>) } } : {}), ...(r.standing ? { standing: { ...(r.standing as Record<string, number>) } } : {}), ...(r.actions ? { actions: actions(r.actions)! } : {}) })),
    triggers: row.triggers.map((t) => ({ id: t.id, when: t.when, ...(t.repeat !== undefined ? { repeat: t.repeat } : {}), actions: actions(t.actions)! })),
    groups: row.groups.map((g) => ({ id: g.id, at: [g.at[0]!, g.at[1]!], spread: g.spread, tag: g.tag, enemies: g.enemies.map((e) => ({ kind: e.kind.id, count: e.count })) })),
    barks,
    barkGates: { ...row.barkGates },
    barkRadius: row.barkRadius,
    witnessRadius: row.witnessRadius,
    logCap: row.logCap,
    scenes: { ...(row.scenes as Record<string, number>) },
    dialogues,
  };
}
